import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api, type CalendarFeed, type CalendarLinks } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy } from '../copy/en-CA.ts';

const c = copy.calendar;

interface Message {
  kind: 'success' | 'error';
  text: string;
}

function describeFailure(err: unknown): string {
  return err instanceof ApiFailure ? err.message : copy.errors.unexpected;
}

/**
 * Calendar subscriptions: a private link per feed that a calendar app
 * checks for changes. Physicians get My schedule and Full schedule;
 * everyone else Full schedule. The server decides which feeds a person may
 * have; this page only shows what it is given.
 */
export function CalendarPage() {
  const [links, setLinks] = useState<CalendarLinks | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .calendarLinks()
      .then(setLinks)
      .catch((err: unknown) => {
        setError(describeFailure(err));
      });
  }, []);

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
        {/* Only someone offered both feeds needs to choose. */}
        {links?.feeds.some((f) => f.feed === 'mine') ? <p>{c.choose}</p> : null}
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {links ? (
        <div className="calendar-feeds">
          {links.feeds.map((f) => (
            <FeedCard key={f.feed} feed={f.feed} path={f.path} onChange={setLinks} />
          ))}
        </div>
      ) : null}
      <section className="card stack calendar-help" aria-labelledby="calendar-privacy">
        <h2 id="calendar-privacy">{c.privacyTitle}</h2>
        {c.privacy.map((p) => (
          <p key={p}>{p}</p>
        ))}
        <h2>{c.howTitle}</h2>
        <ul>
          {c.how.map((h) => (
            <li key={h.who}>
              <strong>{h.who}</strong> {h.what}
            </li>
          ))}
        </ul>
        <p className="muted">{c.timing}</p>
        <p className="muted">{c.coverage}</p>
      </section>
    </>
  );
}

function FeedCard({ feed, path, onChange }: { feed: CalendarFeed; path: string | null; onChange: (links: CalendarLinks) => void }) {
  const names = c.feeds[feed];
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const url = path ? `${window.location.origin}${path}` : null;
  const webcal = path ? `webcal://${window.location.host}${path}` : null;
  const inputId = `calendar-link-${feed}`;

  const act = async (run: () => Promise<CalendarLinks>, done: string | null) => {
    setBusy(true);
    setMessage(null);
    try {
      onChange(await run());
      if (done) setMessage({ kind: 'success', text: done });
    } catch (err) {
      setMessage({ kind: 'error', text: describeFailure(err) });
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setMessage({ kind: 'success', text: c.copied });
    } catch {
      // Clipboard access can be refused (browser settings, older browsers):
      // select the text so the person can copy it themselves.
      input.current?.focus();
      input.current?.select();
      setMessage({ kind: 'error', text: c.copyFailed });
    }
  };

  return (
    <section className="card stack" aria-labelledby={`calendar-${feed}`}>
      <div>
        <h2 id={`calendar-${feed}`}>{names.name}</h2>
        <p className="muted">{names.about}</p>
      </div>
      {url && webcal ? (
        <>
          <div className="field">
            <label htmlFor={inputId}>{c.link(names.name)}</label>
            <input
              id={inputId}
              ref={input}
              className="input calendar-url"
              readOnly
              value={url}
              onFocus={(e) => {
                e.currentTarget.select();
              }}
            />
          </div>
          <div className="row calendar-actions">
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                void copyLink();
              }}
            >
              {c.copy}
            </button>
            <a className="btn" href={webcal}>
              {c.open}
            </a>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                if (window.confirm(c.confirmRenew(names.name))) void act(() => api.makeCalendarLink(feed), c.renewed);
              }}
            >
              {c.renew}
            </button>
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(c.confirmTurnOff(names.name))) void act(() => api.turnOffCalendarLink(feed), c.turnedOff);
              }}
            >
              {c.turnOff}
            </button>
          </div>
          <p className="muted small">{c.openHint}</p>
        </>
      ) : (
        <div className="row">
          <span className="muted">{c.none}</span>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() => {
              void act(() => api.makeCalendarLink(feed), null);
            }}
          >
            {busy ? c.working : c.create}
          </button>
        </div>
      )}
      {message ? <Notice kind={message.kind}>{message.text}</Notice> : null}
    </section>
  );
}
